import { z } from "zod";
import { BlockIdSchema, LinkSchema, MediaRefSchema } from "./common";

/**
 * Presentation overrides every block accepts (edited in the designer's Style tab).
 * Renderers apply these on the block wrapper; content components stay unaware.
 */
export const BlockStyleSchema = z
  .object({
    background: z.enum(["auto", "white", "muted", "navy", "gold", "custom"]).default("auto"),
    background_color: z.string().max(32).optional(),
    padding_y: z.enum(["none", "sm", "md", "lg", "xl"]).default("md"),
    text_align: z.enum(["inherit", "left", "center", "right"]).default("inherit"),
    text_color: z.string().max(32).optional(),
    max_width: z.enum(["default", "narrow", "wide", "full"]).default("default"),
    hide_on_mobile: z.boolean().default(false),
    hide_on_desktop: z.boolean().default(false),
    animation: z.enum(["none", "fade-up"]).default("none"),
    fill_viewport: z.boolean().default(false),
  })
  .prefault({});
export type BlockStyle = z.infer<typeof BlockStyleSchema>;

const base = {
  id: BlockIdSchema,
  hidden: z.boolean().default(false),
  /** Optional HTML anchor id for in-page navigation. */
  anchor: z.string().max(64).optional(),
  /** Optional label shown in the designer's layers panel. */
  label: z.string().max(60).optional(),
  style: BlockStyleSchema,
};

/* ---------- free layout: a 12-column grid of absolutely placed elements (Wix-style) ---------- */

export const FREE_COLS = 12;
/** Positions/sizes are grid units at quarter-cell precision. */
export const FREE_UNIT = 0.25;

export const FreeElementStyleSchema = z.object({
  bg: z.string().max(32).optional(),
  color: z.string().max(32).optional(),
  align: z.enum(["left", "center", "right"]).optional(),
  valign: z.enum(["top", "middle", "bottom"]).optional(),
  padding: z.number().min(0).max(200).optional(),
  radius: z.number().min(0).max(200).optional(),
  shadow: z.enum(["none", "sm", "md", "lg"]).optional(),
  border: z.string().max(64).optional(),
  fit: z.enum(["cover", "contain"]).optional(),
  object_position: z.string().max(32).optional(),
  variant: z.enum(["primary", "gold", "outline", "link"]).optional(),
  size: z.enum(["sm", "md", "lg"]).optional(),
  opacity: z.number().min(0).max(1).optional(),
  z_index: z.number().int().min(0).max(50).optional(),
  mobile_hide: z.boolean().optional(),
});
export type FreeElementStyle = z.infer<typeof FreeElementStyleSchema>;

const freeBase = {
  id: BlockIdSchema,
  x: z
    .number()
    .min(0)
    .max(FREE_COLS - FREE_UNIT),
  y: z.number().min(0).max(400),
  w: z.number().min(0.5).max(FREE_COLS),
  h: z.number().min(0.5).max(400),
  style: FreeElementStyleSchema.prefault({}),
};

export const FreeElementSchema = z.discriminatedUnion("type", [
  z.object({ ...freeBase, type: z.literal("text"), html: z.string().max(20_000).default("") }),
  z.object({
    ...freeBase,
    type: z.literal("image"),
    image: MediaRefSchema.optional(),
    href: z.string().max(2000).optional(),
  }),
  z.object({
    ...freeBase,
    type: z.literal("button"),
    label: z.string().max(80).default("Learn more"),
    href: z.string().max(2000).default("/"),
  }),
  z.object({
    ...freeBase,
    type: z.literal("video"),
    src: z.string().max(2000).default(""),
    poster: MediaRefSchema.optional(),
  }),
  z.object({ ...freeBase, type: z.literal("divider") }),
  z.object({ ...freeBase, type: z.literal("spacer") }),
]);
export type FreeElement = z.infer<typeof FreeElementSchema>;
export type FreeElementType = FreeElement["type"];

export const FreeLayoutBlockSchema = z.object({
  ...base,
  type: z.literal("freeform"),
  /** Row height in px for one grid unit; columns are the container width / 12. */
  row_height: z.number().int().min(8).max(200).default(40),
  gap: z.number().int().min(0).max(64).default(16),
  min_rows: z.number().int().min(1).max(200).default(6),
  elements: z.array(FreeElementSchema).max(100).default([]),
});

export const HeroBlockSchema = z.object({
  ...base,
  type: z.literal("hero"),
  eyebrow: z.string().max(80).optional(),
  headline: z.string().min(1).max(160),
  subheadline: z.string().max(400).optional(),
  primary_cta: LinkSchema.optional(),
  secondary_cta: LinkSchema.optional(),
  image: MediaRefSchema.optional(),
  align: z.enum(["left", "center"]).default("center"),
  /** "split" puts the image beside the copy on wide screens (needs `image`). */
  layout: z.enum(["stacked", "split"]).default("stacked"),
  /** Short trust markers under the CTAs ("Works offline", "HMAC-signed QR"). */
  trust_points: z.array(z.string().min(1).max(60)).max(5).optional(),
});

export const StatItemSchema = z.object({
  value: z.string().min(1).max(24),
  label: z.string().min(1).max(80),
  detail: z.string().max(160).optional(),
});

/** Proof band: three or four large figures with labels. */
export const StatsBlockSchema = z.object({
  ...base,
  type: z.literal("stats"),
  heading: z.string().max(160).optional(),
  tone: z.enum(["light", "navy"]).default("light"),
  items: z.array(StatItemSchema).min(2).max(4),
});

export const StepItemSchema = z.object({
  icon: z.string().max(40).optional(),
  title: z.string().min(1).max(80),
  body: z.string().max(400),
});

/** Numbered "how it works" sequence. */
export const StepsBlockSchema = z.object({
  ...base,
  type: z.literal("steps"),
  heading: z.string().max(160).optional(),
  intro: z.string().max(400).optional(),
  items: z.array(StepItemSchema).min(2).max(6),
  cta: LinkSchema.optional(),
});

export const FeatureItemSchema = z.object({
  icon: z.string().max(40).optional(),
  title: z.string().min(1).max(80),
  body: z.string().max(400),
});

export const FeaturesBlockSchema = z.object({
  ...base,
  type: z.literal("features"),
  heading: z.string().max(160).optional(),
  intro: z.string().max(400).optional(),
  columns: z.union([z.literal(2), z.literal(3), z.literal(4)]).default(3),
  items: z.array(FeatureItemSchema).min(1).max(12),
});

export const ScreenshotsBlockSchema = z.object({
  ...base,
  type: z.literal("screenshots"),
  heading: z.string().max(160).optional(),
  items: z
    .array(
      z.object({
        image: MediaRefSchema,
        caption: z.string().max(200).optional(),
      }),
    )
    .min(1)
    .max(12),
});

export const VideoBlockSchema = z.object({
  ...base,
  type: z.literal("video"),
  heading: z.string().max(160).optional(),
  provider: z.enum(["youtube", "vimeo", "file"]),
  /** YouTube/Vimeo id, or a media URL for `file`. */
  src: z.string().min(1).max(2000),
  poster: MediaRefSchema.optional(),
  caption: z.string().max(200).optional(),
});

export const PricingBlockSchema = z.object({
  ...base,
  type: z.literal("pricing"),
  heading: z.string().max(160).optional(),
  intro: z.string().max(400).optional(),
  /** Plan codes only; tiers are hydrated from `/public/cms/pricing` at read time. */
  plan_codes: z.array(z.string().min(1).max(40)).min(1).max(6),
  show_yearly: z.boolean().default(true),
  highlight_code: z.string().max(40).optional(),
  footnote: z.string().max(300).optional(),
});

export const FaqBlockSchema = z.object({
  ...base,
  type: z.literal("faq"),
  heading: z.string().max(160).optional(),
  items: z
    .array(
      z.object({
        question: z.string().min(1).max(200),
        answer: z.string().min(1).max(2000),
      }),
    )
    .min(1)
    .max(30),
});

export const CtaBlockSchema = z.object({
  ...base,
  type: z.literal("cta"),
  headline: z.string().min(1).max(160),
  body: z.string().max(400).optional(),
  primary_cta: LinkSchema,
  secondary_cta: LinkSchema.optional(),
  tone: z.enum(["navy", "gold", "light"]).default("navy"),
});

export const LogosBlockSchema = z.object({
  ...base,
  type: z.literal("logos"),
  heading: z.string().max(160).optional(),
  items: z
    .array(
      z.object({
        image: MediaRefSchema,
        name: z.string().max(80),
        href: z.url().optional(),
      }),
    )
    .min(1)
    .max(24),
});

export const TestimonialBlockSchema = z.object({
  ...base,
  type: z.literal("testimonial"),
  heading: z.string().max(160).optional(),
  items: z
    .array(
      z.object({
        quote: z.string().min(1).max(600),
        author: z.string().min(1).max(80),
        role: z.string().max(120).optional(),
        avatar: MediaRefSchema.optional(),
      }),
    )
    .min(1)
    .max(12),
});

export const RichtextBlockSchema = z.object({
  ...base,
  type: z.literal("richtext"),
  /** Sanitised HTML produced by the admin editor. */
  html: z.string().max(200_000),
  width: z.enum(["prose", "wide"]).default("prose"),
});

export const BlockSchema = z.discriminatedUnion("type", [
  HeroBlockSchema,
  StatsBlockSchema,
  StepsBlockSchema,
  FeaturesBlockSchema,
  ScreenshotsBlockSchema,
  VideoBlockSchema,
  PricingBlockSchema,
  FaqBlockSchema,
  CtaBlockSchema,
  LogosBlockSchema,
  TestimonialBlockSchema,
  RichtextBlockSchema,
  FreeLayoutBlockSchema,
]);

export type HeroBlock = z.infer<typeof HeroBlockSchema>;
export type StatsBlock = z.infer<typeof StatsBlockSchema>;
export type StepsBlock = z.infer<typeof StepsBlockSchema>;
export type FeaturesBlock = z.infer<typeof FeaturesBlockSchema>;
export type ScreenshotsBlock = z.infer<typeof ScreenshotsBlockSchema>;
export type VideoBlock = z.infer<typeof VideoBlockSchema>;
export type PricingBlock = z.infer<typeof PricingBlockSchema>;
export type FaqBlock = z.infer<typeof FaqBlockSchema>;
export type CtaBlock = z.infer<typeof CtaBlockSchema>;
export type LogosBlock = z.infer<typeof LogosBlockSchema>;
export type TestimonialBlock = z.infer<typeof TestimonialBlockSchema>;
export type RichtextBlock = z.infer<typeof RichtextBlockSchema>;
export type FreeLayoutBlock = z.infer<typeof FreeLayoutBlockSchema>;
export type Block = z.infer<typeof BlockSchema>;
export type BlockType = Block["type"];
/** Editor-facing input type (defaults optional). */
export type BlockInput = z.input<typeof BlockSchema>;

export const BLOCK_TYPES: readonly BlockType[] = BlockSchema.options.map(
  (o) => o.shape.type.value,
) as BlockType[];
